import { DestroyRef, Injectable, NgZone, effect, inject, signal } from "@angular/core";
import { Cookies, CookieService } from "src/app/services/cookie.service";
import { SettingsService } from "src/app/v2/pages/contracts/services/settings.service";

/** How often we re-check for the asynchronously loaded Hotjar script. */
const HOTJAR_POLL_INTERVAL_MS = 250;

/** How long we wait for the Hotjar script before giving up quietly (blocked by adblockers etc.). */
const HOTJAR_MAX_WAIT_MS = 10_000;

/** The `surveyTrigger` cookie value that starts the Hotjar logged-out survey. */
const LOGGED_OUT_SURVEY_TRIGGER = "LOGGED_OUT_FROM_SMART_PENSION";

/**
 * Sends the customer's public pension status to Hotjar as the `HasPublicPension` user attribute,
 * exactly once per app bootstrap, so surveys can be targeted on it. Also sets the cookie that starts
 * the logged-out survey.
 */
@Injectable({
  providedIn: "root",
})
export class HotjarService {
  private readonly settingsService = inject(SettingsService);
  private readonly cookieService = inject(CookieService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  /** True once the async Hotjar script has registered `window.hj`. */
  private readonly hotjarReady = signal(false);

  /** Guards the effect against re-running: Hotjar is identified exactly once per app bootstrap. */
  private identifySent = false;

  private pollId: ReturnType<typeof setInterval> | undefined;
  private giveUpId: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    // Fires once the script is present and the settings request has produced a value. A failed
    // request leaves the resource without a value, so nothing is sent — never a misleading `false`.
    effect(() => {
      if (this.identifySent || !this.hotjarReady() || !this.settingsService.userSettings.hasValue()) {
        return;
      }

      this.identifySent = true;
      this.stopWaitingForHotjar();

      window.hj("identify", this.cookieService.getOrCreateStbId(), {
        HasPublicPension: this.settingsService.hasPublicPension(),
      });
    });

    this.destroyRef.onDestroy(() => this.stopWaitingForHotjar());
  }

  /**
   * Starts watching for the Hotjar script.
   *
   * Nothing is requested until the script is actually present, so a user with an adblocker or without
   * analytics consent causes no network traffic on our side either.
   */
  public init(): void {
    if (isHotjarAvailable()) {
      this.markHotjarReady();
      return;
    }

    this.pollForHotjarScript();
  }

  /**
   * Sets the cookie that starts the Hotjar logged-out survey. It is written synchronously, so it is
   * set before the logout redirect leaves the page.
   */
  public setLogoutSurveyCookie(): void {
    this.cookieService.setCookie(Cookies.SurveyTrigger, LOGGED_OUT_SURVEY_TRIGGER);
  }

  /**
   * Asks `SettingsService` for the user settings rather than fetching them here. It owns the single
   * root-scoped `httpResource`, and the Avtaler page reads that same resource, so whichever consumer
   * gets there first triggers one request and the other reuses it.
   */
  private markHotjarReady(): void {
    this.hotjarReady.set(true);
    this.settingsService.ensureLoaded();
  }

  /**
   * Polls outside the Angular zone so the ticks don't each trigger a change detection pass,
   * and re-enters the zone on the single transition that matters.
   */
  private pollForHotjarScript(): void {
    // A second init() would orphan the first interval id, leaking it for the life of the app.
    if (this.pollId !== undefined) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.pollId = setInterval(() => {
        if (!isHotjarAvailable()) {
          return;
        }

        this.stopWaitingForHotjar();
        this.ngZone.run(() => this.markHotjarReady());
      }, HOTJAR_POLL_INTERVAL_MS);

      // Give up quietly rather than polling for the lifetime of the app.
      this.giveUpId = setTimeout(() => this.stopWaitingForHotjar(), HOTJAR_MAX_WAIT_MS);
    });
  }

  private stopWaitingForHotjar(): void {
    clearInterval(this.pollId);
    clearTimeout(this.giveUpId);
    this.pollId = undefined;
    this.giveUpId = undefined;
  }
}

function isHotjarAvailable(): boolean {
  return typeof window.hj === "function";
}
